import {createContentEditableHtmlEditor} from '@frontapp/editor-html';
import {useEditor} from '@frontapp/editor-html/lib/react';
import {SelectOrdinal, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {usePushEvent} from '../../../../../srcCommon/push/pushContext';
import {ContentEditableBox} from '../../../../components/common/boxedInputs/contentEditableBox';
import {OkCancelDialog} from '../../../../components/common/dialogs/okCancelDialog';
import {Paragraph} from '../../../../components/common/paragraph';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {buildTeammateDisplayName} from '../../../../helpers/modelHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {ChannelModel, ChannelSmtpModel} from '../../../../models/api/channelModel';
import {ApiDnsValidationResult} from '../../../../models/api/smtpDomainModel';
import {postAsync} from '../../../../services/http/http';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {getHasSendgridSubuser} from '../../../../store/featureFlags/featureFlagsSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../../../banners/settingsBannersContext';
import {SettingsAccordionFieldLine} from '../../../common/settingsAccordionFieldLine';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {StyledSettingsFormTextBox} from '../../../common/settingsWrapper';
import {useFetchDnsProvidersbyDomain, useFetchDnsRecords} from '../../../fetchers/dns';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsSpfDkimFieldProps extends SettingsCommonChannelFieldProps<ChannelSmtpModel> {}

/*
 * Style.
 */

const StyledAnchor = styled.a`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade40};
  cursor: pointer;

  &:hover {
    color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade50};
  }
`;

const StyledRecordTitleDiv = styled.div`
  margin-top: 10px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
  line-height: 1.16;

  color: ${(p) => p.theme.greys.shade90};
  margin-bottom: 18px;
  display: flex;
  justify-content: space-between;
`;
const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: flex;
  column-gap: 6px;
  color: ${(p) => p.theme.palette[PaletteColorsEnum.RED].shade40};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

const StyledButtonsWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 10px;
`;

/*
 * Component.
 */

export const SettingsSpfDkimField: FC<SettingsSpfDkimFieldProps> = (props) => {
  const {dnsProvider} = useFetchDnsProvidersbyDomain(props.edited);

  return (
    <SettingsAccordionFieldLine
      title={<Trans id="settingschannel-spfdkim-title">Improve delivery rates SPF / DKIM</Trans>}
      subtitle={getInstructionsForDnsProvider(dnsProvider)}
      isSubtitleMultiline
    >
      <SpfDkimContent {...props} dnsProvider={dnsProvider} />
    </SettingsAccordionFieldLine>
  );
};

const StyledForm = styled.div`
  margin-top: 10px;
`;

const SpfDkimContent = (props: SettingsSpfDkimFieldProps & {dnsProvider: string}) => {
  const hasSendgridSubuser = useAppSelector(getHasSendgridSubuser);
  const [showPreviousDNS, setShowPreviousDNS] = useState(false);

  const {dnsRecords} = useFetchDnsRecords(props.edited, showPreviousDNS);

  const validateDnsSettings = async () => {
    postAsync(`${props.edited.url}/validate_smtp_dns?subuser_transition=${showPreviousDNS}`);
  };
  const [recordErrors, setRecordErrors] = useState<Array<string>>(['', '', '']);
  const [validRecords, setValidRecords] = useState<Array<boolean>>([false, false, false]);
  usePushEvent('validate_dns_result', ApiDnsValidationResult, (result) => {
    _(result.result)
      .entries()
      .forEach((entry, index) => {
        const [, value] = entry;

        if (value.valid) {
          setValidRecords((valid) => {
            const newValid = [...valid];
            newValid[index] = value.valid;

            return newValid;
          });
        }

        setRecordErrors((err) => {
          const newErr = [...err];
          newErr[index] = value.reason || '';

          return newErr;
        });
      });
  });

  const {copy} = useClipboard();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const onItHelpClick = () => {
    setIsDialogOpen(true);
  };

  const maybeRenderDialog = () => {
    if (!isDialogOpen) {
      return null;
    }

    return (
      <SpfDkimHelpDialog
        onRequestClose={() => {
          setIsDialogOpen(false);
        }}
        channel={props.edited}
      />
    );
  };

  return (
    <>
      {maybeRenderDialog()}
      <div>
        {hasSendgridSubuser && (
          <SettingsFieldLine
            title={<Trans id="settingschannel-spfdkim-previous">Display previous DNS settings</Trans>}
            hasNoBorder
            content={<Toggle onChange={setShowPreviousDNS} isChecked={showPreviousDNS} />}
          />
        )}
        {dnsRecords?.map((record, index) => {
          const renderRight = () => {
            if (recordErrors[index]) {
              return (
                <StyledTooltipCoordinator render={() => recordErrors[index]}>
                  <Icon name="warningFilledSmall" />
                  <Trans id="settingschannel-spfdkim-checkfailed">Check failed!</Trans>
                </StyledTooltipCoordinator>
              );
            }

            if (validRecords[index]) {
              return (
                <StyledTooltipCoordinator>
                  <Icon name="checkmarkCircleSmall" />
                  <Trans id="settingschannel-spfdkim-verified">Verified!</Trans>
                </StyledTooltipCoordinator>
              );
            }

            return null;
          };

          return (
            <div key={record.value}>
              <StyledRecordTitleDiv>
                <SelectOrdinal
                  value={index + 1}
                  one="#st record"
                  two="#nd record"
                  few="#rd record"
                  other="#th record"
                />
                {renderRight()}
              </StyledRecordTitleDiv>
              <StyledSettingsFormTextBox
                $width={600}
                size={VisualSizesEnum.MEDIUM}
                onCopy={() => {
                  copy(record.name);
                }}
                label={<Trans id="settingschannel-spfdkim-name">Name</Trans>}
                value={record.name}
              />
              <StyledSettingsFormTextBox
                $width={600}
                size={VisualSizesEnum.MEDIUM}
                onCopy={() => {
                  copy(record.type);
                }}
                label={<Trans id="settingschannel-spfdkim-type">Type</Trans>}
                value={record.type}
              />
              <StyledSettingsFormTextBox
                $width={600}
                size={VisualSizesEnum.MEDIUM}
                onCopy={() => {
                  copy(record.value);
                }}
                label={<Trans id="settingschannel-spfdkim-value">Value</Trans>}
                value={record.value}
              />
            </div>
          );
        })}
        <StyledButtonsWrapper>
          <Button
            size={VisualSizesEnum.MEDIUM}
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={validateDnsSettings}
          >
            <Trans id="settingschannel-spfdkim-checkdns">Check DNS settings</Trans>
          </Button>
          <Button size={VisualSizesEnum.SMALL} variant={ButtonVariantsEnum.LINK} onClick={onItHelpClick}>
            <Trans id="settingschannel-spfdkim-askit">Ask your IT Team to make this change.</Trans>{' '}
          </Button>
        </StyledButtonsWrapper>
      </div>
    </>
  );
};

interface SpfDkimHelpDialogProps {
  onRequestClose: Handler;
  channel: ChannelModel;
}
export const SpfDkimHelpDialog = (props: SpfDkimHelpDialogProps) => {
  const user = useAppSelector(getUser);
  const {dnsRecords} = useFetchDnsRecords(props.channel, false);
  const {dnsProvider} = useFetchDnsProvidersbyDomain(props.channel);

  const editor = useEditor(() => {
    const html = ReactDOMServer.renderToStaticMarkup(
      <div>
        <p>Hello!</p>
        <p>
          {buildTeammateDisplayName(user)} is in the process of setting up Front (
          <a href="https://front.com" target="_blank" rel="noopener noreferrer">
            https://front.com
          </a>
          ) to manage shared email addresses and we need your help with one small thing.
        </p>
        <p>
          To improve email delivery rates and reduce the risk of emails being marked as spam, we need you to
          make this settings change. Can you please add these DNS records? This change will authorize Front to
          send emails on {user.givenName}&apos;s behalf.
        </p>
        <p>
          <a href={getDnsProviderLink(dnsProvider)} target="_blank" rel="noopener noreferrer">
            Read instructions
          </a>{' '}
          for setting up DNS records{dnsProvider && ` ${dnsProvider}`}.
        </p>
        {dnsRecords?.map((record, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <p key={index}>
            <b>DNS Record:</b>
            <br />
            <b>Name: </b>
            <span>{record.name}</span>
            <br />
            <b>Type: </b>
            <span>{record.type}</span>
            <br />
            <b>Value: </b>
            <span>{record.value}</span>
            <br />
          </p>
        ))}
        <p>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a>Once you are done, please click here and we&apos;ll verify your DNS settings.</a>
        </p>
        <p>If you have any questions, just reply. We&apos;re here to help. Thanks so much!</p>
      </div>,
    );

    const newEditor = createContentEditableHtmlEditor({
      initialHtml: html,
      isInitialHtmlClean: true,
    });

    return newEditor;
  }, [dnsProvider, dnsRecords, user]);
  const [emailAlertState, setEmailAlertState] = useState(() => ({
    email: '',
    cc: user.email,
  }));

  const {showBanner} = useSettingsBanners();
  const company = useAppSelector(getCompany);
  const onOkClick = async (options: {email: string; cc: string}) => {
    await postAsync(`${company.url}/channels/dns_help_email`, {
      channel_id: props.channel.id,
      to: options.email,
      cc: options.cc,
    });
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: (
        <Trans id="settingschannel-spfdkim-success">
          We&apos;ve sent a message to your IT team with instructions on how to set up SPF/DKIM.
        </Trans>
      ),
    });
    props.onRequestClose();
  };

  return (
    <OkCancelDialog
      width={650}
      title={<Trans id="settingschannel-spfdkim-sendemail">Send SPF/DKIM Help Email</Trans>}
      cancelButtonLabel={<Trans id="settingschannel-spfdkim-skipstep">Skip this step for now</Trans>}
      okButtonLabel={<Trans id="settingschannel-spfdkim-send">Send</Trans>}
      onRequestClose={props.onRequestClose}
      onOkClick={() => {
        onOkClick(emailAlertState);
      }}
      isOkDisabled={emailAlertState.email.trim().length < 0}
    >
      <Paragraph>
        <Trans id="settingschannel-spfdkim-askit-copy">
          If you need help from your IT team to make the DNS change, we&apos;ve prepared an email that you can
          send.
        </Trans>
        <br />
        <Trans id="settingschannel-spfdkim-askit-copy2">Just fill out the To: field and hit Send.</Trans>
      </Paragraph>
      <StyledForm>
        <TextBox
          value={emailAlertState.email}
          onChange={(value) =>
            setEmailAlertState((prev) => ({
              ...prev,
              email: value,
            }))
          }
          size={VisualSizesEnum.MEDIUM}
          label={<Trans id="settingschannel-spfdkim-askit-to">To</Trans>}
        />
        <TextBox
          value={emailAlertState.cc}
          onChange={(value) =>
            setEmailAlertState((prev) => ({
              ...prev,
              cc: value,
            }))
          }
          size={VisualSizesEnum.MEDIUM}
          label={<Trans id="settingschannel-spfdkim-askit-cc">CC</Trans>}
        />
        <TextBox
          value="Adding DNS records for Front"
          size={VisualSizesEnum.MEDIUM}
          label={<Trans id="settingschannel-spfdkim-askit-subject">Subject</Trans>}
        />
        <ContentEditableBox editor={editor} />
      </StyledForm>
    </OkCancelDialog>
  );
};

const instructionsLinkByProvider: Record<string, string> = {
  // Keep in sync with the switch statement in the function below
  aws: 'https://help.frontapp.com/t/x17l65/setting-up-sendgrid-spfdkim-dns-records-in-aws',
  cloudflare: 'https://help.frontapp.com/t/367ztc/setting-up-sendgrid-spfdkim-dns-records-in-cloudflare',
  godaddy: 'https://help.frontapp.com/t/80290w/setting-up-sendgrid-spfdkim-dns-records-in-godaddy',
};

const defaultInstructionsLink =
  'https://help.frontapp.com/t/80249r/removing-via-sendgrid-net-from-outgoing-emails';

function getDnsProviderLink(dnsProvider: string) {
  return instructionsLinkByProvider[dnsProvider] || defaultInstructionsLink;
}

function getDnsProviderDisplayName(dnsProvider: string) {
  if (instructionsLinkByProvider[dnsProvider]) {
    switch (dnsProvider) {
      case 'aws':
        return 'AWS';
      case 'cloudflare':
        return 'Cloudflare';
      case 'godaddy':
        return 'GoDaddy';
      default: {
        return null;
      }
    }
  }

  return null;
}

function getInstructionsForDnsProvider(dnsProvider: string) {
  const providerName = getDnsProviderDisplayName(dnsProvider);
  const helpCenterLink = getDnsProviderLink(dnsProvider);

  if (providerName) {
    return (
      <Trans id="settingschannel-spfdkim-instructionsprovider">
        To reduce the risk of your emails being marked as spam, you&apos;ll need to add SPF/DKIM records to
        your DNS. This will authorize Front to send emails on your behalf. This will authorize Front to send
        emails on your behalf. Here are{' '}
        <StyledAnchor target="_blank" href={`${helpCenterLink}`} rel="noreferrer noopener">
          instructions
        </StyledAnchor>{' '}
        for how to set up DNS records for ${providerName}. This is advanced material that would best be done
        by an IT admin or someone who is familiar with DNS records.
      </Trans>
    );
  }

  return (
    <Trans id="settingschannel-spfdkim-instructionsnoprovider">
      To reduce the risk of your emails being marked as spam, you&apos;ll need to add SPF/DKIM records to your
      DNS. This will authorize Front to send emails on your behalf. This will authorize Front to send emails
      on your behalf. Here are{' '}
      <StyledAnchor target="_blank" href={`${helpCenterLink}`} rel="noreferrer noopener">
        instructions
      </StyledAnchor>{' '}
      for how to set up DNS records. This is advanced material that would best be done by an IT admin or
      someone who is familiar with DNS records.
    </Trans>
  );
}
